import { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { orgID, post, useData } from '@yingbaba/api';
import type { Notification, Page } from '@yingbaba/types';
import { Bell, Mail } from '@yingbaba/icons';
import {
  Button,
  Empty,
  ErrorNotice,
  Modal,
  PageTitle,
  Pagination,
  QueryState,
  useToast,
} from '@yingbaba/ui';
import { datetime } from '@yingbaba/utils';
import { EmailPreferences } from './EmailPreferences';

export function Notifications() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') === 'unread' ? 'unread' : '';
  const page = Math.min(10000, Math.max(1, Math.floor(Number(params.get('page')) || 1)));
  const [emailOpen, setEmailOpen] = useState(false);
  const q = useData<Page<Notification> & { unread_count?: number }>(
    `/workspace/notifications?page=${page}&status=${status}`,
  );
  const [pending, setPending] = useState<string[]>([]);
  const locks = useRef(new Set<string>());
  const [error, setError] = useState<unknown>(null);
  const qc = useQueryClient(),
    toast = useToast();
  async function mark(items: Notification[]) {
    const ids = items.filter((n) => !n.read && !locks.current.has(n.id)).map((n) => n.id);
    if (!ids.length) return;
    ids.forEach((id) => locks.current.add(id));
    setPending([...locks.current]);
    setError(null);
    try {
      if (ids.length === 1) await post(`/notifications/${ids[0]}/read`);
      else await post('/notifications/read-batch', { ids });
      await qc.invalidateQueries({
        predicate: (q) => /^\/workspace\/(notifications|overview)/.test(String(q.queryKey[0])),
      });
    } catch (e) {
      setError(e);
      toast('未能更新已读状态，可稍后重试。');
    } finally {
      ids.forEach((id) => locks.current.delete(id));
      setPending([...locks.current]);
    }
  }
  function filter(value: string) {
    setParams((p) => {
      value ? p.set('status', value) : p.delete('status');
      p.delete('page');
      return p;
    });
  }
  const unread = q.data?.items.filter((n) => !n.read) || [];
  return (
    <>
      <PageTitle
        title="系统通知"
        description="查看协作、资料审核和客服处理的进展。"
        action={
          <Button variant="secondary" onClick={() => setEmailOpen(true)}>
            <Mail size={16} />
            邮件提醒
          </Button>
        }
      />
      <div className="notification-work-toolbar">
        <nav className="tabs work-section-tabs" aria-label="通知筛选">
          <button
            type="button"
            className={!status ? 'active' : ''}
            aria-current={!status ? 'page' : undefined}
            onClick={() => filter('')}
          >
            全部通知
          </button>
          <button
            type="button"
            className={status ? 'active' : ''}
            aria-current={status ? 'page' : undefined}
            onClick={() => filter('unread')}
          >
            未读{q.data?.unread_count ? ` ${q.data.unread_count}` : ''}
          </button>
        </nav>
        <Button
          variant="secondary"
          size="sm"
          disabled={!unread.length || !!pending.length}
          onClick={() => void mark(unread)}
        >
          本页标为已读
        </Button>
      </div>
      <Modal open={emailOpen} onOpenChange={setEmailOpen} title="业务邮件提醒">
        {emailOpen && <EmailPreferences key={orgID()} onSaved={() => setEmailOpen(false)} />}
      </Modal>
      <ErrorNotice error={error} />
      <QueryState query={q}>
        {!q.data?.items.length ? (
          <Empty
            title={
              status
                ? q.data?.unread_count
                  ? '当前页暂无未读通知'
                  : '未读通知已处理完'
                : '暂无通知'
            }
            description="新的协作、审核和服务进展会在这里提醒你。"
            action={
              status && !!q.data?.unread_count ? (
                <Button variant="secondary" onClick={() => filter('unread')}>
                  查看未读通知
                </Button>
              ) : status || page > 1 ? (
                <Button variant="secondary" onClick={() => setParams({})}>
                  查看全部通知
                </Button>
              ) : undefined
            }
          />
        ) : (
          <section className="panel notification-work-list" aria-label="通知列表">
            {q.data.items.map((n) => (
              <article className={`notification-work-item${n.read ? ' is-read' : ''}`} key={n.id}>
                <span className="icon-tile">
                  <Bell size={19} />
                </span>
                <div className="notification-work-content">
                  <Link to={n.href || '/notifications'} onClick={() => void mark([n])}>
                    <strong>{n.title}</strong>
                  </Link>
                  <p>{n.content}</p>
                  <time className="small muted">{datetime(n.created_at)}</time>
                </div>
                {!n.read ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={pending.includes(n.id)}
                    onClick={() => void mark([n])}
                  >
                    标为已读
                  </Button>
                ) : (
                  <span className="small muted">已读</span>
                )}
              </article>
            ))}
          </section>
        )}
      </QueryState>
      <Pagination
        page={page}
        size={q.data?.page_size || 30}
        total={q.data?.total || 0}
        onPage={(v) =>
          setParams((p) => {
            v > 1 ? p.set('page', String(v)) : p.delete('page');
            return p;
          })
        }
      />
    </>
  );
}
